// UI shell geometry (IMPLEMENTATION §2.6 "Sheets and cards", "Hit targets", UX §1.5–1.6). PURE: every rect in
// client CSS px from the production computeLayout; every number from ui.json. Sheets and cards rise from the
// top edge of the tab bar (the tab bar stays visible):
//   Shop sheet    clamp(shop.sheetHsRatio·Hs, sheetMin, sheetMax)         320 / 280 / 280 at Hs 763 / 664 / 548
//   Gallery card  clamp(sheets.galleryHsRatio·Hs, galleryMin, galleryMax) 382 / 332 / 320
//   other cards   counter + tabs (120 Regular / 108 Compact); "More" → sheets.moreRatio·Hs
//   Settings      Hs − HUD (from the HUD's bottom down to the tab bar)
//   toasts        44 tall, 8 px above the counter (or above an open panel), newest at the bottom
import type { UiData } from '../data/schema.ts';
import type { Layout, Rect } from '../layout/computeLayout.ts';

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

export function shopSheetHeight(Hs: number, shop: UiData['shop']): number {
  return clamp(Math.round(shop.sheetHsRatio * Hs), shop.sheetMin, shop.sheetMax);
}

export function galleryCardHeight(Hs: number, sheets: UiData['sheets']): number {
  return clamp(Math.round(sheets.galleryHsRatio * Hs), sheets.galleryMin, sheets.galleryMax);
}

/** Info / Machine / Crate / Ghost / Expansion card height: counter + tabs; "More" = half the screen. */
export function cardHeight(l: Layout, sheets: UiData['sheets'], more: boolean): number {
  const base = l.counter.h + l.tabs.h;
  return more ? Math.max(base, Math.round(sheets.moreRatio * l.Hs)) : base;
}

/** A panel of height `h` rising from the top edge of the tab bar. */
export function panelRect(l: Layout, h: number): Rect {
  const top = Math.max(l.column.y, l.tabs.y - h);
  return { x: l.column.x, y: top, w: l.column.w, h: l.tabs.y - top };
}

/** Settings: Hs − HUD, i.e. from the HUD's bottom edge down to the tab bar (which stays visible). */
export function settingsRect(l: Layout): Rect {
  const top = l.hud.y + l.hud.h;
  return { x: l.column.x, y: top, w: l.column.w, h: l.tabs.y - top };
}

/** The clip area panels slide in and out of (everything above the tab bar). */
export function panelClip(l: Layout): Rect {
  return { x: l.column.x, y: l.column.y, w: l.column.w, h: l.tabs.y - l.column.y };
}

/**
 * The goal chip's hit box: the chip row grown to at least `minHit` tall, centred (UX: "Hit area is 44: it
 * extends 4 px into the gaps"; Compact's 32 px row grows 6 px each way, into the HUD's lower edge and the gap).
 */
export function chipHitRect(l: Layout, minHit: number): Rect {
  const grow = Math.max(0, (minHit - l.chip.h) / 2);
  return { x: l.chip.x, y: l.chip.y - grow, w: l.chip.w, h: l.chip.h + 2 * grow };
}

/** Toast boxes, newest at the bottom, `gap` px above `bottomY` (the counter's or an open panel's top). */
export function toastRects(
  l: Layout,
  n: number,
  bottomY: number,
  t: UiData['toasts'],
  gutter: number,
): Rect[] {
  const out: Rect[] = [];
  const w = l.column.w - 2 * gutter;
  for (let i = 0; i < n; i++) {
    const fromBottom = n - 1 - i;
    const y = bottomY - t.gapAboveCounter - t.height - fromBottom * (t.height + t.gapAboveCounter);
    out.push({ x: l.column.x + gutter, y, w, h: t.height });
  }
  return out;
}

/**
 * A phone held in landscape gets the RotateOverlay (UX §1.6): a landscape viewport too low for the portrait
 * layout (below overlay.rotateMaxHs; every phone in landscape is). A tablet in landscape (Hs ≥ 540: Regular,
 * 96 px cells) and a desktop window keep the centred column (UX §1.6).
 */
export function wantsRotate(vw: number, vh: number, rotateMaxHs: number): boolean {
  return vw > vh && vh < rotateMaxHs;
}

/** Equal slots across a bar (tab bar, Build bar). */
export function barSlots(r: Rect, n: number): Rect[] {
  const w = r.w / n;
  return Array.from({ length: n }, (_, i) => ({ x: r.x + i * w, y: r.y, w, h: r.h }));
}
